import React from "react";
import {
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Overflow,
  OverflowItem,
  Toolbar,
  ToolbarButton,
  Tooltip,
  useOverflowMenu,
  useIsOverflowItemVisible,
} from "@fluentui/react-components";
import {
  MoreHorizontalRegular,
  ArrowClockwiseRegular,
  ArrowLeftRegular,
  MailUnreadRegular,
  MailAddRegular,
  MailMultipleRegular,
  ArrowUndoRegular,
  ChatMultipleRegular,
  FolderRegular,
  PauseRegular,
  PlayRegular,
  TimerRegular,
  BeakerRegular,
  PeopleRegular,
  EmojiRegular,
  InfoRegular,
  QuestionCircleRegular,
} from "@fluentui/react-icons";
import {
  toolbarIconClass,
  refreshActiveIconClasses,
  refreshIconClasses,
  backIconClasses,
  followIconClass,
  overflowButtonClass,
} from "./styles";

// --- Types ---

type ToolbarMenuSubItem = {
  key: string;
  label: string;
  icon: React.JSX.Element;
  onClick: () => void;
  visible?: boolean;
  disabled?: boolean;
};

type ToolbarItemConfig = {
  id: string;
  priority: number;
  label: string;
  icon: React.JSX.Element;
  onClick?: () => void;
  disabled?: boolean;
  visible?: boolean;
  menuItems?: ToolbarMenuSubItem[];
};

// --- Hook: build items array ---

type UseToolbarItemsParams = {
  isHomePage: boolean;
  onRefresh: () => void;
  isRefetching: boolean;
  onBack: () => void;
  isFollowed: boolean | undefined;
  onFollow: () => void;
  onUnfollow: () => void;
  onCatchUp: () => void;
  onFile: () => void;
  onSummarise: () => void;
  onPauseNavigate: (buttonText: string, duration: string) => void;
  onPauseOff: () => void;
  showTest: boolean;
  onTest: () => void;
  isTestPending: boolean;
  onPriorityContacts: () => void;
  onFeedback: () => void;
  onAbout: () => void;
  onHelp: () => void;
  trackEvent: (name: string) => void;
};

const useToolbarItems = (params: UseToolbarItemsParams): ToolbarItemConfig[] => {
  const {
    isHomePage,
    onRefresh,
    isRefetching,
    onBack,
    isFollowed,
    onFollow,
    onUnfollow,
    onCatchUp,
    onFile,
    onSummarise,
    onPauseNavigate,
    onPauseOff,
    showTest,
    onTest,
    isTestPending,
    onPriorityContacts,
    onFeedback,
    onAbout,
    onHelp,
    trackEvent,
  } = params;

  const navItem: ToolbarItemConfig = isHomePage
    ? {
        id: "refresh",
        priority: 9,
        label: "Refresh",
        icon: (
          <ArrowClockwiseRegular
            className={isRefetching ? refreshActiveIconClasses : refreshIconClasses}
          />
        ),
        onClick: onRefresh,
      }
    : {
        id: "back",
        priority: 9,
        label: "Back",
        icon: <ArrowLeftRegular className={backIconClasses} />,
        onClick: onBack,
      };

  return [
    navItem,
    {
      id: "follow",
      priority: 8,
      label: "Follow",
      icon: <MailUnreadRegular className={followIconClass} />,
      menuItems: [
        {
          key: "follow-conversation",
          label: "Follow Conversation",
          icon: <MailAddRegular className={followIconClass} />,
          onClick: onFollow,
          visible: !isFollowed,
        },
        {
          key: "unfollow-conversation",
          label: "Unfollow Conversation",
          icon: <ArrowUndoRegular className={followIconClass} />,
          onClick: onUnfollow,
          visible: !!isFollowed,
        },
        {
          key: "catch-up",
          label: "Catch up",
          icon: <MailMultipleRegular className={toolbarIconClass} />,
          onClick: onCatchUp,
        },
      ],
    },
    {
      id: "file",
      priority: 7,
      label: "File",
      icon: <FolderRegular className={toolbarIconClass} />,
      onClick: onFile,
    },
    {
      id: "summarise",
      priority: 6,
      label: "Summarise",
      icon: <ChatMultipleRegular className={toolbarIconClass} />,
      onClick: onSummarise,
    },
    {
      id: "pause-mail",
      priority: 6,
      label: "Pause Mail",
      icon: <PauseRegular className={toolbarIconClass} />,
      menuItems: [
        {
          key: "pause-on",
          label: "Pause on",
          icon: <PauseRegular className={toolbarIconClass} />,
          onClick: () => {
            trackEvent("Menu click: Pause on");
            onPauseNavigate("Pause", "9999999");
          },
        },
        {
          key: "pause-off",
          label: "Pause off",
          icon: <PlayRegular className={toolbarIconClass} />,
          onClick: onPauseOff,
        },
        {
          key: "pause-30",
          label: "Pause for 30 minutes",
          icon: <TimerRegular className={toolbarIconClass} />,
          onClick: () => {
            trackEvent("Menu click: Pause for 30 minutes");
            onPauseNavigate("Pause for 30 minutes", "30");
          },
        },
        {
          key: "pause-60",
          label: "Pause for 1 hour",
          icon: <TimerRegular className={toolbarIconClass} />,
          onClick: () => {
            trackEvent("Menu click: Pause for 1 hour");
            onPauseNavigate("Pause for 1 hour", "60");
          },
        },
        {
          key: "pause-90",
          label: "Pause for 90 minutes",
          icon: <TimerRegular className={toolbarIconClass} />,
          onClick: () => {
            trackEvent("Menu click: Pause for 90 minutes");
            onPauseNavigate("Pause for 90 minutes", "90");
          },
        },
        {
          key: "pause-120",
          label: "Pause for 2 hours",
          icon: <TimerRegular className={toolbarIconClass} />,
          onClick: () => {
            trackEvent("Menu click: Pause for 2 hours");
            onPauseNavigate("Pause for 2 hours", "120");
          },
        },
      ],
    },
    {
      id: "test",
      priority: 5,
      label: "Test",
      icon: <BeakerRegular className={isTestPending ? refreshActiveIconClasses : toolbarIconClass} />,
      onClick: onTest,
      disabled: isTestPending,
      visible: showTest,
    },
    {
      id: "priority-contacts",
      priority: 4,
      label: "Priority Contacts",
      icon: <PeopleRegular className={toolbarIconClass} />,
      onClick: onPriorityContacts,
    },
    {
      id: "feedback",
      priority: 3,
      label: "Feedback",
      icon: <EmojiRegular className={toolbarIconClass} />,
      onClick: onFeedback,
    },
    {
      id: "about",
      priority: 2,
      label: "About",
      icon: <InfoRegular className={toolbarIconClass} />,
      onClick: onAbout,
    },
    {
      id: "help",
      priority: 1,
      label: "Help",
      icon: <QuestionCircleRegular className={toolbarIconClass} />,
      onClick: onHelp,
    },
  ];
};

const getVisibleSubItems = (item: ToolbarItemConfig): ToolbarMenuSubItem[] =>
  item.menuItems?.filter((sub) => sub.visible !== false) ?? [];

const getVisibleItems = (items: ToolbarItemConfig[]): ToolbarItemConfig[] =>
  items.filter((item) => item.visible !== false);

// --- Toolbar item renderer (visible items in the toolbar) ---

const ToolbarItemRenderer: React.FC<{ item: ToolbarItemConfig }> = ({ item }) => {
  if (item.visible === false) return null;

  if (item.menuItems) {
    const visibleSubItems = getVisibleSubItems(item);
    if (visibleSubItems.length === 0) return null;

    // <Menu> is a context provider with no DOM element, so OverflowItem can't
    // inject its measurement ref into it. Wrapping in a <span> gives OverflowItem
    // a real DOM element to measure and show/hide.
    return (
      <OverflowItem id={item.id} priority={item.priority}>
        <span>
          <Menu>
            <Tooltip content={item.label} relationship="label">
              <MenuTrigger disableButtonEnhancement>
                <ToolbarButton aria-label={item.label} icon={item.icon} disabled={item.disabled} />
              </MenuTrigger>
            </Tooltip>
            <MenuPopover>
              <MenuList>
                {visibleSubItems.map((sub) => (
                  <MenuItem key={sub.key} icon={sub.icon} onClick={sub.onClick} disabled={sub.disabled}>
                    {sub.label}
                  </MenuItem>
                ))}
              </MenuList>
            </MenuPopover>
          </Menu>
        </span>
      </OverflowItem>
    );
  }

  return (
    <OverflowItem id={item.id} priority={item.priority}>
      <Tooltip content={item.label} relationship="label">
        <ToolbarButton
          aria-label={item.label}
          icon={item.icon}
          onClick={item.onClick}
          disabled={item.disabled}
        />
      </Tooltip>
    </OverflowItem>
  );
};

// --- Overflow menu item (renders only when the item has overflowed) ---

const OverflowMenuItem: React.FC<{ item: ToolbarItemConfig }> = ({ item }) => {
  const isVisible = useIsOverflowItemVisible(item.id);

  if (item.visible === false || isVisible) return null;

  if (item.menuItems) {
    const visibleSubItems = getVisibleSubItems(item);
    if (visibleSubItems.length === 0) return null;

    return (
      <>
        {visibleSubItems.map((sub) => (
          <MenuItem key={sub.key} icon={sub.icon} onClick={sub.onClick} disabled={sub.disabled}>
            {sub.label}
          </MenuItem>
        ))}
      </>
    );
  }

  return (
    <MenuItem icon={item.icon} onClick={item.onClick} disabled={item.disabled}>
      {item.label}
    </MenuItem>
  );
};

// --- Overflow menu button (the "..." trigger) ---
// The button must always be rendered so its ref stays attached to the Overflow
// context, which uses it to measure the button's width when deciding when items
// overflow. Using visibility:hidden keeps it in the layout flow without showing it.

const OverflowMenuButton: React.FC<{ items: ToolbarItemConfig[] }> = ({ items }) => {
  const { ref, isOverflowing } = useOverflowMenu<HTMLButtonElement>();
  const visibleItems = getVisibleItems(items);

  return (
    <Menu>
      <MenuTrigger disableButtonEnhancement>
        <ToolbarButton
          ref={ref}
          aria-label="More options"
          icon={<MoreHorizontalRegular className={toolbarIconClass} />}
          className={overflowButtonClass}
          style={{ visibility: isOverflowing ? "visible" : "hidden" }}
        />
      </MenuTrigger>
      {isOverflowing && (
        <MenuPopover>
          <MenuList>
            {visibleItems.map((item) => (
              <OverflowMenuItem key={item.id} item={item} />
            ))}
          </MenuList>
        </MenuPopover>
      )}
    </Menu>
  );
};

// --- Composed responsive toolbar ---

type ResponsiveToolbarProps = {
  items: ToolbarItemConfig[];
  overflowPadding?: number;
  toolbarAriaLabel?: string;
  className?: string;
};

const ResponsiveToolbar: React.FC<ResponsiveToolbarProps> = ({
  items,
  overflowPadding = 40,
  toolbarAriaLabel = "Toolbar",
  className,
}) => {
  const visibleItems = getVisibleItems(items);

  return (
    <Overflow overflowDirection="end" padding={overflowPadding}>
      <Toolbar aria-label={toolbarAriaLabel} className={className}>
        {visibleItems.map((item) => (
          <ToolbarItemRenderer key={item.id} item={item} />
        ))}
        <OverflowMenuButton items={visibleItems} />
      </Toolbar>
    </Overflow>
  );
};

export { useToolbarItems, ToolbarItemRenderer, OverflowMenuButton, ResponsiveToolbar };
export type { ToolbarItemConfig, ResponsiveToolbarProps };
